import classNames from "classnames";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { HelperText } from "components/DesignSystem/HelperText/HelperText";
import { FormikForm } from "components/FormikForm/FormikForm";
import { Cross } from "components/icons/Cross";
import { Pinned } from "components/icons/Pinned";
import { TrashSimple } from "components/icons/TrashSimple";
import ToolTip from "components/design/toolTip";
import { AnnotationDoc } from "./DocumentAnnotationPanel";
import { Button } from "components/DesignSystem/Button/Button";
import BankWarningCircle from "static/images/BankWarningCircle.svg";

const EMPTY_QUESTION_ERROR = "This is a required field";
const MIN_QUESTION_ROWS = 3;
const CHARS_PER_QUESTION_ROW = 60;

export type SidebarQuestion = {
  id: string;
  text: string;
  docIndex: number | null;
  x: number | null;
  y: number | null;
};

type QuestionCardProps = {
  question: SidebarQuestion;
  number: number;
  isActive: boolean;
  docs: AnnotationDoc[];
  onClick: () => void;
  variant?: "admin" | "answer";
  onChange?: (text: string) => void;
  onDelete?: () => void;
  isPinMode?: boolean;
  onTogglePinMode?: () => void;
  onRemoveAnnotation?: () => void;
  /** When set, the annotation chip becomes a button that opens the document.
   * Used on mobile, where both panes cannot be shown side by side. */
  onViewDocument?: () => void;
  hasError?: boolean;
  errorText?: string;
  questionTextClassName?: string;
  children?: React.ReactNode;
};

export const QuestionCard = ({
  question,
  number,
  isActive,
  docs,
  onClick,
  variant = "admin",
  onChange,
  onDelete,
  isPinMode,
  onTogglePinMode,
  onRemoveAnnotation,
  onViewDocument,
  hasError = false,
  errorText = EMPTY_QUESTION_ERROR,
  questionTextClassName,
  children,
}: QuestionCardProps) => {
  const pinnedDoc =
    question.docIndex !== null ? docs[question.docIndex] ?? null : null;
  const isAnnotated = pinnedDoc !== null;
  const isAnswerVariant = variant === "answer";

  const questionLineCount = question.text
    .split("\n")
    .reduce(
      (total, line) =>
        total + Math.max(1, Math.ceil(line.length / CHARS_PER_QUESTION_ROW)),
      0
    );
  const questionRows = Math.max(MIN_QUESTION_ROWS, questionLineCount);

  const handleRemoveAnnotationClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemoveAnnotation?.();
  };

  const questionLabel = (
    <span
      className={classNames(
        "t-flex t-items-center t-justify-center t-w-6 t-h-6 t-rounded-full t-text-body",
        {
          "t-text-white t-bg-purple-50": isActive,
          "t-text-neutral-50 t-bg-surface-grey": !isActive,
        }
      )}
    >
      {number}
    </span>
  );

  return (
    <div
      id={`question-${question.id}`}
      className={classNames({ "t-cursor-pointer": isAnnotated })}
      onClick={isAnnotated ? onClick : undefined}
    >
      <div
        className={classNames(
          "t-flex t-flex-col t-gap-2 t-p-2.5 t-rounded-xl t-border t-border-solid t-bg-surface t-transition-colors",
          {
            "t-border-purple-10 t-shadow-purple-glow": isActive && !hasError,
            "t-border-red": hasError,
            "t-border-neutral-10": !isActive && !hasError,
            "hover:t-bg-surface-lighter-grey": isAnnotated && !isActive,
          }
        )}
      >
        {isAnswerVariant ? (
          <>
            <div className="t-flex t-items-center t-justify-between t-gap-2">
              <div className="t-flex t-items-center t-gap-2 t-min-w-0">
                {questionLabel}
              </div>

              {pinnedDoc &&
                (onViewDocument ? (
                  <button
                    type="button"
                    aria-label={`View ${pinnedDoc.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewDocument();
                    }}
                    className="all:unset t-flex t-shrink-0 t-max-w-[220px] t-min-w-0 t-cursor-pointer t-items-center t-gap-1 t-rounded t-border t-border-solid t-border-neutral-10 t-bg-surface-lighter-grey t-px-2 t-py-1 t-text-caption t-text-purple"
                  >
                    <Pinned size="12" />
                    <span className="t-truncate">
                      Pinned to {pinnedDoc.name}
                    </span>
                  </button>
                ) : (
                  <ToolTip text={pinnedDoc.name} side="top">
                    <div className="t-flex t-items-center t-gap-1 t-text-caption t-text-neutral-40 t-min-w-0 t-shrink-0 t-max-w-[220px]">
                      <Pinned size="12" />
                      <span className="t-truncate">
                        Pinned to {pinnedDoc.name}
                      </span>
                    </div>
                  </ToolTip>
                ))}
            </div>

            <p
              className={classNames(
                "t-m-0 t-text-body-sm",
                questionTextClassName ?? "t-text-text-30"
              )}
            >
              {question.text}
            </p>

            {children && (
              <div className="t-flex t-flex-col t-gap-2">{children}</div>
            )}

            {hasError && (
              <div className="t-flex t-items-center t-gap-1">
                <img
                  src={BankWarningCircle}
                  alt="BankWarningCircle"
                  className="t-size-3"
                />
                <p className="t-m-0 t-text-red t-text-caption">{errorText}</p>
              </div>
            )}
          </>
        ) : (
          <FormikForm
            initialValues={{ text: question.text }}
            onSubmit={() => {}}
            enableReinitialize
          >
            {() => (
              <>
                <div className="t-flex t-items-center t-justify-between t-gap-2 t-pb-2">
                  <div
                    className="t-flex t-items-center t-gap-2 t-min-w-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {questionLabel}

                    {pinnedDoc ? (
                      <div className="t-flex t-items-center t-gap-1 t-text-body-sm t-text-text-30 t-min-w-0">
                        <span className="t-truncate">
                          Pinned to {pinnedDoc.name}
                        </span>
                        {onRemoveAnnotation && (
                          <ToolTip text="Unpin from document">
                            <Button
                              onClick={handleRemoveAnnotationClick}
                              size="xsmall"
                              customType="ghost_icon"
                            >
                              <Cross size="10" />
                            </Button>
                          </ToolTip>
                        )}
                      </div>
                    ) : (
                      onTogglePinMode &&
                      docs.length > 0 && (
                        <Button
                          customType="link"
                          size="xsmall"
                          onClick={onTogglePinMode}
                        >
                          Pin to document
                        </Button>
                      )
                    )}
                  </div>

                  {onDelete && (
                    <Button
                      customType="ghost"
                      size="xsmall"
                      onClick={onDelete}
                      leftIcon={<TrashSimple height={12} width={12} />}
                    >
                      Delete
                    </Button>
                  )}
                </div>

                <div onClick={(e) => e.stopPropagation()}>
                  <TextArea
                    name="text"
                    placeholder="Type your question here"
                    rows={questionRows}
                    size="small"
                    autoResize={false}
                    style={{ maxHeight: "12rem", overflowY: "auto" }}
                    value={question.text}
                    onChange={
                      onChange ? (e) => onChange(e.target.value) : undefined
                    }
                    disabled={!onChange}
                  />
                  {hasError && (
                    <HelperText variant="error">
                      {EMPTY_QUESTION_ERROR}
                    </HelperText>
                  )}
                </div>

                {isPinMode && !pinnedDoc && (
                  <p className="t-text-caption t-text-purple t-m-0">
                    Click anywhere on the document to pin this question
                  </p>
                )}
              </>
            )}
          </FormikForm>
        )}
      </div>
    </div>
  );
};
